import { TypedLazyQueryTrigger, TypedUseLazyQueryStateResult } from "@reduxjs/toolkit/query/react";

import { DataSourceRegistryPath, pathToNameMap } from "features/data-source/data-source-registry";
import { prepareNewApproachArgs } from "features/data-source/data-source.utils";
import { useLazyDataSourceOldApproach } from "features/data-source/useLazyDataSourceOldApproach";
import { useShouldUseNewApproach } from "features/data-source/useShouldUseNewApproach";
import {
    DataSourceParameter,
    GetDataSourceOptionsV2,
    useLazyGetDataSourceQuery,
} from "store/services/data-source.service";

const useLazyDataSourceNewApproach = <T = unknown>(dataSourcePath: DataSourceRegistryPath) => {
    const { useNewApproach } = useShouldUseNewApproach(dataSourcePath);

    const dataSourceName = pathToNameMap[dataSourcePath];
    const [triggerOld, resultOld] = useLazyDataSourceOldApproach<T>(dataSourceName);
    const [triggerNew, resultNew] = useLazyGetDataSourceQuery();

    const trigger: TypedLazyQueryTrigger<any, any, any> = (
        parameters?: DataSourceParameter[],
        preferCacheValue?: boolean
    ) => {
        if (useNewApproach) {
            const args = prepareNewApproachArgs(dataSourcePath, parameterArrayToRecord(parameters));
            return triggerNew(args as GetDataSourceOptionsV2, preferCacheValue);
        } else {
            return triggerOld(parameters, preferCacheValue);
        }
    };

    const result = useNewApproach ? resultNew : resultOld;

    return [trigger, result] as [typeof trigger, TypedUseLazyQueryStateResult<T, any, any>];
};

const parameterArrayToRecord = (parameters?: DataSourceParameter[]): Record<string, string | number | boolean> => {
    if (!parameters) return {};
    return Object.fromEntries(parameters.map((param) => [param.name, param.value]));
};

export { useLazyDataSourceNewApproach };
